/**
 * Auth Navigation Hooks
 *
 * Centralized navigation helpers for authentication flows.
 * Reduces duplication and makes navigation behavior easy to change globally.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthNavigationHelpers {
  navigateToHome: () => void;
  navigateToLogin: () => void;
  reloadAt: (url: string) => void;
}

/**
 * Hook for authentication-related navigation
 *
 * @returns Navigation helper functions
 *
 * @example
 * ```tsx
 * const { navigateToHome, navigateToLogin } = useAuthNavigation();
 *
 * // After successful login
 * navigateToHome();
 *
 * // After logout
 * navigateToLogin();
 * ```
 */
export function useAuthNavigation(): AuthNavigationHelpers {
  const navigate = useNavigate();

  const navigateToHome = useCallback(() => {
    void navigate('/');
  }, [navigate]);

  const navigateToLogin = useCallback(() => {
    void navigate('/login');
  }, [navigate]);

  // A full page load rather than a router push: whatever the new page does next
  // runs without the user's tap attached to it.
  const reloadAt = useCallback((url: string) => {
    window.location.assign(url);
  }, []);

  return {
    navigateToHome,
    navigateToLogin,
    reloadAt
  };
}
